import React, { useState, useEffect } from 'react';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { productAPI, mediatorProductAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { formatDate } from '../../utils/dateTime';

const AdminDealRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [formData, setFormData] = useState({
    lessAmount: ''
  });

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await productAPI.getPendingDealRequests();
      setRequests(response.data.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch deal requests:', error);
      toast.error('Failed to load deal requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAssignAndApprove = async (e) => {
    if (e) e.preventDefault();
    if (!formData.lessAmount) {
      toast.error('Please enter Less Amount');
      return;
    }

    setProcessingId(selectedRequest.id);
    try {
      // Step 1: Assign product to mediator
      await mediatorProductAPI.assignProduct({
        productId: selectedRequest.productId,
        mediatorId: selectedRequest.mediatorId,
        lessAmount: parseFloat(formData.lessAmount),
        active: true
      });

      // Step 2: Update status to APPROVED
      await productAPI.updateDealRequestStatus(selectedRequest.id, 'APPROVED', 'Mediator assigned and deal approved');
      
      toast.success('Mediator assigned and deal approved successfully');
      setShowModal(false);
      setSelectedRequest(null);
      setFormData({ lessAmount: '' });
      fetchRequests();
    } catch (error) {
      console.error('Failed to assign and approve:', error);
      toast.error(error.response?.data?.message || 'Failed to complete approval');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (e) => {
    if (e) e.preventDefault();
    if (!adminNote.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setProcessingId(selectedRequest.id);
    try {
      await productAPI.updateDealRequestStatus(selectedRequest.id, 'REJECTED', adminNote);
      toast.success('Request rejected successfully');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setAdminNote('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setAdminNote('');
    setShowRejectModal(true);
  };

  const openApproveModal = (request) => {
    setSelectedRequest(request);
    setFormData({ lessAmount: '' });
    setShowModal(true);
  };

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', overflow: 'auto' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', margin: '0 0 4px 0' }}>Deal Requests</h1>
            <p style={{ fontSize: '14px', color: '#64748b' }}>Manage mediator requests for product deals.</p>
          </div>
          <Button variant="secondary" onClick={fetchRequests} disabled={loading}>
            <Icon name="refresh-cw" size={16} /> Refresh
          </Button>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <div className="overflow-x-auto table-scroll-container">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Mediator</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Available Slots</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Request Date</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center' }}>Loading requests...</td></tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '60px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <Icon name="req" size={40} color="#e2e8f0" />
                        <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>No pending requests.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  requests.map(req => (
                    <tr key={req.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>{req.mediatorName}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{req.mediatorEmail}</div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>{req.productName}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{req.brandName}</div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontSize: '13px' }}>Rating: <strong>{req.remainingRatingSlots}</strong></div>
                        <div style={{ fontSize: '13px' }}>Review: <strong>{req.remainingReviewSlots}</strong></div>
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#475569' }}>
                        {formatDate(req.createdAt)}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button 
                            variant="primary" 
                            size="sm" 
                            style={{ background: '#10b981' }}
                            onClick={() => openApproveModal(req)}
                          >
                            Approve
                          </Button>
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            style={{ background: '#ef4444', color: '#fff' }}
                            onClick={() => openRejectModal(req)}
                          >
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Approve Request Modal */}
      <Modal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={`Approve Deal Request - ${selectedRequest?.productName}`} 
        width={500}
      >
        <div style={{ padding: '24px' }}>
          <form onSubmit={handleAssignAndApprove}>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Product</p>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}>
                {selectedRequest?.productName} ({selectedRequest?.productCode || selectedRequest?.brandName})
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Mediator</p>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}>
                {selectedRequest?.mediatorName} ({selectedRequest?.mediatorEmail})
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Less Amount (₹)</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.lessAmount}
                onChange={(e) => setFormData({ lessAmount: e.target.value })}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  borderRadius: '8px', 
                  border: '1px solid #d1d5db', 
                  fontSize: '14px',
                  display: 'block' 
                }}
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" loading={processingId === selectedRequest?.id}>Confirm & Approve</Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Reject Request Modal */}
      <Modal 
        isOpen={showRejectModal} 
        onClose={() => setShowRejectModal(false)} 
        title={`Reject Deal Request - ${selectedRequest?.productName}`} 
        width={500}
      >
        <div style={{ padding: '24px' }}>
          <form onSubmit={handleReject}>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Product</p>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}>
                {selectedRequest?.productName} ({selectedRequest?.productCode || selectedRequest?.brandName})
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Mediator</p>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}>
                {selectedRequest?.mediatorName} ({selectedRequest?.mediatorEmail})
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Reason for Rejection</label>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Enter rejection reason..."
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: '1px solid #d1d5db', 
                  fontSize: '14px',
                  display: 'block',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button type="button" variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
              <Button type="submit" loading={processingId === selectedRequest?.id} style={{ background: '#ef4444' }}>Confirm Rejection</Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default AdminDealRequests;
